import { isEqual } from 'lodash'
import { EditorState } from 'prosemirror-state'
import { type EditorView } from 'prosemirror-view'
import { ANCHOR_DATA_ATTR, MENTIONS_PLUGIN_KEY } from './constants'
import { schema } from '../../Schema'
import { Provider } from 'react-redux'
import reduxStore from 'redux/store'
import { getArticleUrl, getAlmanacUrlFor, getUserProfilePath } from 'utils/urlUtils'
import { Suggestions } from './Suggestions'
import { Mentionable } from './Types'
import { AlgoliaClientWrapper } from 'providers/AlgoliaClientProvider'
import { FloatingDropdown } from '../../components/FloatingDropdown/FloatingDropdown'
import { MentionType } from 'common/ProseInput/Types'
import { createRoot, Root } from 'react-dom/client'
import { QueryClient, QueryClientProvider } from 'react-query'

export class MentionsView {
  initialized: boolean = false
  view: EditorView
  container: HTMLDivElement
  root: Root
  queryClient: QueryClient

  constructor(view: EditorView, queryClient: QueryClient) {
    this.view = view
    this.container = document.createElement('div')
    this.queryClient = queryClient
    Object.assign(this.container.style, { visibility: 'hidden' })
    this.root = createRoot(this.container)
  }

  initialize() {
    this.view.dom.parentNode?.appendChild(this.container)
    this.mountComponent('')
    this.initialized = true
  }

  update = (view: EditorView, prevState: EditorState) => {
    if (!this.initialized) {
      this.initialize()
    }
    this.view = view
    const prev = MENTIONS_PLUGIN_KEY.getState(prevState)
    const next = MENTIONS_PLUGIN_KEY.getState(view.state)

    if (!prev || !next) {
      return
    }

    const opening = !prev.active && next.active
    const moving = prev.active && next.active && !isEqual(prev.range, next.range)
    const closing = prev.active && !next.active

    if (opening || moving) {
      this.displayPopover(next.text ?? '')
    }

    if (closing) {
      this.displayPopover('')
    }
  }

  destroy = () => {
    this.root.render(<></>)
  }

  private mountComponent = (text: string = '') => {
    const anchor = this.view.dom.querySelector(`[${ANCHOR_DATA_ATTR}]`)
    this.root.render(
      <QueryClientProvider client={this.queryClient}>
        <Provider store={reduxStore}>
          <AlgoliaClientWrapper>
            <FloatingDropdown anchor={anchor} root={anchor?.closest('#comment-prompt')}>
              <Suggestions view={this.view} query={text} onSelect={this.insertMention} />
            </FloatingDropdown>
          </AlgoliaClientWrapper>
        </Provider>
      </QueryClientProvider>,
    )
  }

  private displayPopover = (text: string) => {
    this.mountComponent(text)
    this.container.style.visibility = 'visible'
  }

  private insertMention = (mentionable: Mentionable) => {
    const state = MENTIONS_PLUGIN_KEY.getState(this.view.state)
    if (!state?.range) {
      return
    }
    const tr = this.view.state.tr
    const attrs =
      mentionable.mentionableType === 'Article'
        ? {
            type: MentionType.Article,
            title: mentionable.title,
            url: getArticleUrl(mentionable.key),
            key: mentionable.key,
          }
        : {
            type: MentionType.User,
            title: mentionable.full_name,
            url: getAlmanacUrlFor(getUserProfilePath({ id: mentionable.user_id })),
            userId: `${mentionable.user_id}`,
          }
    tr.replaceWith(state.range.from, state.range.to, [schema.nodes.smart_link.create(attrs), schema.text(' ')])
    this.view.dispatch(tr)
    this.view.focus()
  }
}
